<template>
  <SubHeading :home="home" :tabs="tabs" :currTab="currTab" @active="handleActive" />

  <!-- breadcrumbs -->
  <nav class="sba-collection-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <router-link to="/claim" class="breadcrumb sba-collection-crumb blue-grey-text text-darken-3">
          <span>Claims Home</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <span class="breadcrumb sba-collection-crumb blue-grey-text pointable">
          <span>Review</span>
        </span>
        <span class="sba-collection-context">Claim Review Dossier</span>
      </div>
    </div>
  </nav>

  <!-- Top -->
  <div id="top"></div>

  <div class="sba-collection-page-surface is-workbench">
    <div class="container claim-view-container">
      <LoadingDiv v-if="!isReady" />

      <div class="claim-review-workbench sba-slide-in" v-else>
        <section class="claim-secondary-header">
          <section class="claim-super-card">
            <button type="button" class="secondary-page-back" title="Back to All Claims" @click.prevent="handleActive(home)">
              <i class="material-icons">keyboard_return</i>
            </button>
            <div class="claim-super-copy">
              <button type="button" class="secondary-parent-link" @click.prevent="handleActive(home)">All Claims</button>
              <small>Back to Claims List</small>
            </div>
          </section>

          <section class="claim-dossier-card">
            <div class="claim-dossier-title">
              <label class="active teal-text">Claims Review</label>
              <h5>{{ capWords(clusterProvider || "Claims Batch") }}</h5>
              <p>
                Review claims, vet where needed, and download payment advice.
              </p>
            </div>

            <div class="claim-dossier-context">
              <span class="context-pill is-related" :class="`is-${clusterType}`" v-if="clusterRelatedLabel">
                {{ clusterRelatedLabel }}
              </span>
              <span class="context-pill">{{ clusterScheme?.toUpperCase() || "SCHEME" }}</span>
              <span class="context-pill">{{ clusterPeriod || "Claims period" }}</span>
              <span class="context-pill" :class="clusterStatusClass">{{ clusterStatusLabel }}</span>
              <span class="context-pill is-payment">{{ clusterPaymentStatus }}</span>
            </div>
          </section>

          <section class="claim-dossier-metrics">
            <div class="metric-cell">
              <span>Sent Total</span>
              <strong class="metric-relaxed-value">&#8358;{{ grossTotalSent(encounters) }}</strong>
            </div>
            <div class="metric-cell">
              <span>Approved Total</span>
              <strong
                class="metric-approved-value"
                :class="{
                  'is-ready': clusterStatus == 'v' || clusterStatus == 'r',
                  'is-pending': !(clusterStatus == 'v' || clusterStatus == 'r'),
                }"
              >
                <template v-if="clusterStatus == 'v' || clusterStatus == 'r'">&#8358;{{ grossTotalApproved(encounters) }}</template>
                <template v-else>Pending vetting</template>
              </strong>
            </div>
            <div class="metric-cell">
              <span>Turnaround</span>
              <strong class="metric-relaxed-value" :class="{ 'tat-counting-chip': clusterTATCounting }">
                {{ clusterTAT }} days<span class="tat-counting-text" v-if="clusterTATCounting">counting</span>
              </strong>
            </div>
            <div class="metric-cell metric-action-cell">
              <button type="button" class="download-action" @click.prevent="handleDownload()">
                <i class="material-icons">download</i>
                <span>Payment Advice</span>
              </button>
            </div>
          </section>

          <section class="claim-related-strip" v-if="relatedClaimLinks.length">
            <button
              v-for="related in relatedClaimLinks"
              :key="related.id"
              type="button"
              class="claim-related-link"
              :class="{
                'is-active': Number(related.id) === Number(cluster?.id),
                'is-draft': related.needsCompletion,
                'is-pending': related.needsReview,
              }"
              @click.prevent="handleRelatedClaim(related)"
            >
              <span>{{ related.label }}</span>
              <small v-if="related.needsCompletion">Continue Add</small>
              <small v-else-if="related.needsReview">Pending</small>
            </button>
          </section>

          <section class="claim-mode-strip">
            <button
              type="button"
              class="claim-mode-close"
              title="Back to Claims List"
              aria-label="Back to Claims List"
              @click.prevent="handleActive(home)"
            >
              <i class="material-icons">close</i>
            </button>
            <div class="claim-mode-actions">
              <button
                v-for="tab in displayTabsState"
                :key="tab.id"
                type="button"
                class="claim-mode-option"
                :class="{ 'is-active': tab.state === currState }"
                @click="handleReview(tab.state)"
              >
                {{ tab.title }}
                <span v-if="tab.title.toLowerCase().includes('all')">({{ encounters.length ? encounters.length : 0 }})</span>
              </button>
              <button type="button" class="claim-mode-option is-active" v-if="!displayTabsState?.find(x => x.state === 'v') && currState === 'v'">
                Review
              </button>
            </div>
          </section>
        </section>

        <section class="claim-secondary-body-frame" :class="{ 'is-vetting': currState == 'v' }">
          <div class="progress" v-if="isLoading">
            <div class="indeterminate"></div>
          </div>

          <!-- Vet claim -->
          <section class="claim-vetting-surface" v-if="currState == 'v'">
            <section class="claim-vetting-tertiary">
              <button
                v-for="tab in claimDetailTabs"
                :key="`vet-${tab.id}`"
                type="button"
                class="claim-vetting-tertiary-link"
                :class="{ 'is-active': tab.state === claimDetailState }"
                @click="handleClaimDetailState(tab.state)"
              >
                {{ tab.title }}
              </button>
            </section>

            <VetClaim
              v-if="claimDetailState === 'review'"
              :encounterId="encounterId"
              :forTariff="cluster"
              :cluster="encounters"
              :clusterStatus="clusterStatus"
              @vetted="handleVetted"
            />

            <section class="claim-vetting-tertiary-panel" v-else-if="claimDetailState === 'benefits'">
              <BenefitsDiv
                v-if="activeVettingBenefitPlanId && activeVettingEncounter?.client_id"
                :key="`benefits-${activeVettingBenefitPlanId}-${activeVettingEncounter.client_id}`"
                :pid="activeVettingBenefitPlanId"
                :cid="activeVettingEncounter.client_id"
              />
              <div class="claim-vetting-empty-note" v-else>
                <i class="material-icons">info_outline</i>
                <span>Benefits are not available for this enrollee.</span>
              </div>
            </section>

            <section class="claim-vetting-tertiary-panel" v-else-if="claimDetailState === 'history'">
              <HistoryClaim
                v-if="activeVettingEncounter?.enrollee_number"
                :key="`history-${activeVettingEncounter.enrollee_number}`"
                :enrolleeNumber="activeVettingEncounter.enrollee_number"
              />
              <div class="claim-vetting-empty-note" v-else>
                <i class="material-icons">info_outline</i>
                <span>Authorization history is not available for this selected claim.</span>
              </div>
            </section>

            <section class="claim-vetting-tertiary-panel" v-else-if="claimDetailState === 'utilization'">
              <UtilizationInsight
                v-if="activeVettingEncounter"
                :key="`utilization-${activeVettingEncounter.id}`"
                :encounter="activeVettingEncounter"
                @reset="handleClaimDetailState('review')"
              />
              <div class="claim-vetting-empty-note" v-else>
                <i class="material-icons">info_outline</i>
                <span>Utilization is not available for this selected claim.</span>
              </div>
            </section>
          </section>

          <!-- Display claims -->
          <template v-if="currState == 'd'">
            <section class="claim-encounter-table" :class="{ 'is-complete': complete || clusterStatus == 'v' || clusterStatus == 'r' }">
              <table class="highlight responsive-table claim-encounter-summary-table">
                <colgroup>
                  <col class="claim-encounter-col-index" />
                  <col class="claim-encounter-col-member" />
                  <col class="claim-encounter-col-diagnosis" />
                  <col class="claim-encounter-col-sent" />
                  <col class="claim-encounter-col-vetted" />
                  <col class="claim-encounter-col-status" />
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Member<br /><small>(Enrollee ID)</small></th>
                    <th>Diagnosis</th>
                    <th>Sent Amount</th>
                    <th>Vetted Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    class="encounter-review-row"
                    :class="{
                      'is-pending': encounter.status == 'p',
                      'is-deleted': encounter.status == 'd',
                    }"
                    v-for="(encounter, index) in encounters"
                    :key="encounter.id"
                    @click.prevent="handleStart(encounter)"
                  >
                    <td class="encounter-index">{{ index + 1 }}</td>

                    <td class="encounter-member">
                      <template v-if="encounter.status == 'd'">
                        <s>{{ capWords(encounter.name) }}</s><br />
                        <small><s>{{ encounter.enrollee_number }}</s></small>
                      </template>
                      <template v-else>
                        {{ capWords(encounter.name) }}<br />
                        <small>{{ encounter.enrollee_number }}</small>
                      </template>
                    </td>

                    <td class="encounter-diagnosis">
                      <s v-if="encounter.status == 'd'">{{ capWords(encounter.diagnosis) }}</s>
                      <span v-else>{{ capWords(encounter.diagnosis) }}</span>
                    </td>

                    <td class="amount-cell">
                      <s v-if="encounter.status == 'd'">&#8358;{{ totalSent(encounter) }}</s>
                      <b v-else>&#8358;{{ totalSent(encounter) }}</b>
                    </td>

                    <td class="amount-cell is-approved" :class="{ 'is-muted': encounter.status == 'p' }">
                      <span v-if="encounter.status == 'p'">-</span>
                      <s v-else-if="encounter.status == 'd'">&#8358;{{ totalApproved(encounter) }}</s>
                      <b v-else :class="claimVettedAmountClass(encounter)">&#8358;{{ totalApproved(encounter) }}</b>
                    </td>

                    <td class="encounter-status">
                      <i
                        class="material-icons pointable"
                        :class="{
                          'yellow-text text-darken-2': encounter.status == 'p',
                          'grey-text text-darken-2': encounter.status != 'p' && encounter.status != 'd',
                          'grey-text': encounter.status == 'd',
                        }"
                        :title="getTitle(encounter)"
                      >
                        {{ encounter.status == "p" ? "watch_later" : "done" }}
                      </i>
                    </td>
                  </tr>

                  <tr class="claim-total-strip">
                    <td colspan="2"></td>
                    <td class="claim-total-label">Total Sent:</td>
                    <td><b class="claim-total-value is-sent">&#8358;{{ grossTotalSent(encounters) }}</b></td>
                    <td>
                      <b class="claim-total-value vetted-total-value" :class="{ 'is-complete': complete, 'is-muted': !complete }">
                        <template v-if="complete">&#8358;{{ grossTotalApproved(encounters) }}</template>
                        <template v-else>N/A</template>
                      </b>
                    </td>
                    <td>
                      <button
                        type="button"
                        class="continue-vetting"
                        :class="{
                          disabled: complete || clusterStatus == 'v' || clusterStatus == 'r',
                        }"
                        @click.stop="handleContinueVetting"
                      >
                        Vet
                        <i class="material-icons">chevron_right</i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>
          </template>

          <!-- Show status -->
          <template v-if="currState == 's'">
            <section class="claim-status-panel">
              <div class="status-title">
                <label class="active teal-text">Payment Status</label>
                <h6>{{ clusterPaymentStatus }}</h6>
                <p>Review the financial position of this claims batch and download the payment advice when needed.</p>
              </div>

              <div class="status-grid">
                <div>
                  <span>Sent Total</span>
                  <strong>&#8358;{{ grossTotalSent(encounters) }}</strong>
                </div>
                <div>
                  <span>Approved Total</span>
                  <strong
                    class="status-approved-value"
                    :class="{
                      'is-ready': clusterStatus == 'v' || clusterStatus == 'r',
                      'is-pending': !(clusterStatus == 'v' || clusterStatus == 'r'),
                    }"
                  >
                    <i class="material-icons status-grid-icon">payments</i>
                    <template v-if="clusterStatus == 'v' || clusterStatus == 'r'">&#8358;{{ grossTotalApproved(encounters) }}</template>
                    <template v-else>Pending</template>
                  </strong>
                </div>
                <div>
                  <span>TAT</span>
                  <strong class="status-tat-value" :class="{ 'is-counting': clusterTATCounting }">
                    <i class="material-icons status-grid-icon">schedule</i>
                    {{ clusterTAT }} days<span class="tat-counting-text" v-if="clusterTATCounting">counting</span>
                  </strong>
                </div>
                <div>
                  <span>Status</span>
                  <strong>{{ clusterStatusLabel }}</strong>
                </div>
              </div>

              <div class="status-actions">
                <button type="button" class="download-action" @click.prevent="handleDownload()">
                  <i class="material-icons">download</i>
                  <span>Download Payment Advice</span>
                </button>

                <button
                  type="button"
                  class="related-delete-action"
                  :disabled="isDeletingRelated"
                  @click.prevent="handleRelatedDeleteOpen"
                  v-if="showRelatedDeleteAction"
                >
                  <i class="material-icons">delete_forever</i>
                  <span>{{ isDeletingRelated ? "Deleting..." : "Delete Related Batch" }}</span>
                </button>

                <span
                  class="special-actions-wrap"
                  :title="specialActionReason || 'Special claim actions'"
                  v-if="showSpecialActions"
                >
                  <a
                    href="#"
                    class="return-menu-trigger"
                    :class="{
                      disabled: !canCreateRelatedCluster || isSubmitting,
                      'dropdown-trigger': canCreateRelatedCluster && !isSubmitting,
                    }"
                    data-target="special-actions-menu"
                    @click.prevent
                  >
                    <span>{{ isSubmitting ? "Creating..." : "Special Actions" }}</span>
                    <i class="material-icons right">more_vert</i>
                  </a>

                  <ul id="special-actions-menu" class="dropdown-content special-actions-menu">
                    <li
                      :class="{ disabledItem: isSpecialActionTypeBlocked('reconciliation') }"
                      :title="specialActionTypeReason('reconciliation')"
                      @click.prevent="handleSpecialActionOpen('reconciliation')"
                    >
                      <a href="#">{{ specialActionTypeLabel('reconciliation') }}</a>
                    </li>
                    <li class="divider" tabindex="-1"></li>
                    <li
                      :class="{ disabledItem: isSpecialActionTypeBlocked('supplementary') }"
                      :title="specialActionTypeReason('supplementary')"
                      @click.prevent="handleSpecialActionOpen('supplementary')"
                    >
                      <a href="#">{{ specialActionTypeLabel('supplementary') }}</a>
                    </li>
                  </ul>
                </span>

                <span class="return-menu-wrap" v-if="showReturnClaims">
                  <a href="#" class="return-menu-trigger dropdown-trigger" data-target="dropmenu">
                    Return Claims
                    <i class="material-icons right">more_vert</i>
                  </a>

                  <ul id="dropmenu" class="dropdown-content">
                    <li :class="{ disabledItem: clusterUserType && clusterUserType !== 'hmo' }" @click.prevent="handleReInput('hmo')">
                      <a href="#">Return Claims <em>to</em> HMO Team</a>
                    </li>
                    <li class="divider" tabindex="-1"></li>
                    <li :class="{ disabledItem: clusterUserType && clusterUserType !== 'provider' }" @click.prevent="handleReInput('provider')">
                      <a href="#">Return Claims <em>to</em> Provider</a>
                    </li>
                    <li class="divider" tabindex="-1"></li>
                  </ul>
                </span>
              </div>
            </section>
          </template>
        </section>
      </div>
    </div>
  </div>

  <div
    class="claim-special-confirm-overlay"
    @click.self="handleSpecialActionClose"
    v-if="pendingSpecialAction"
  >
    <section
      class="claim-special-confirm-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="claim-special-confirm-title"
    >
      <div class="claim-special-confirm-content">
        <label class="active teal-text">Special Action</label>
        <h4 id="claim-special-confirm-title">{{ specialActionModal.title }}</h4>
        <p>{{ specialActionModal.message }}</p>
        <p class="claim-special-confirm-next">{{ specialActionModal.next }}</p>
      </div>
      <div class="claim-special-confirm-footer">
        <button
          type="button"
          class="claim-special-confirm-btn is-cancel"
          :disabled="isSubmitting"
          @click.prevent="handleSpecialActionClose"
        >
          Cancel
        </button>
        <button
          type="button"
          class="claim-special-confirm-btn is-confirm"
          :disabled="isSubmitting"
          @click.prevent="handleSpecialActionConfirm"
        >
          {{ isSubmitting ? "Creating..." : specialActionModal.confirm }}
        </button>
      </div>
    </section>
  </div>

  <div
    class="claim-special-confirm-overlay"
    @click.self="handleRelatedDeleteClose"
    v-if="pendingRelatedDelete"
  >
    <section
      class="claim-special-confirm-modal is-danger"
      role="dialog"
      aria-modal="true"
      aria-labelledby="claim-related-delete-title"
    >
      <div class="claim-special-confirm-content">
        <label class="active red-text text-darken-2">Delete Related Batch</label>
        <h4 id="claim-related-delete-title">Delete {{ clusterRelatedLabel }}?</h4>
        <p>
          This is irreversible. The encounters and claims in this related batch will be declined,
          approved quantities and approved amounts will be set to zero, and the batch will be marked
          as completed with a zero-value paid record.
        </p>
        <p class="claim-special-confirm-next is-danger">
          After deletion, this batch will no longer block a new special action from the original claim.
        </p>
      </div>
      <div class="claim-special-confirm-footer">
        <button
          type="button"
          class="claim-special-confirm-btn is-cancel"
          :disabled="isDeletingRelated"
          @click.prevent="handleRelatedDeleteClose"
        >
          Cancel
        </button>
        <button
          type="button"
          class="claim-special-confirm-btn is-danger"
          :disabled="isDeletingRelated"
          @click.prevent="handleRelatedDeleteConfirm"
        >
          {{ isDeletingRelated ? "Deleting..." : "Delete Permanently" }}
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, onMounted, onUpdated, computed, watch, watchEffect, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import useModuleNavigation from "@/composables/useModuleNavigation";
import getItemsOfItems from "@/composables/getItemsOfItems";
import postItem from "@/composables/postItem";
import putItem from "@/composables/putItem";
import SubHeading from "@/components/HeaderSubDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import {
  toMonthYear,
  capWordsFx,
} from "@/composables/fxLibrary/coreFx.js";
import { getFullMonthName } from "@/composables/fxLibrary/months";
import {
  grossTotalSentFx,
  grossTotalApprovedFx,
  totalSentFx,
  totalApprovedFx,
  paymentStatusFx,
} from "@/composables/calFunction/cal";
import {
  downloadExcelFx,
} from "@/composables/reportFunction/downloader";
import VetClaim from "@/components/claim/VetClaimComponent.vue";
import BenefitsDiv from "@/components/plan/ShowBenefitComponent.vue";
import HistoryClaim from "@/components/claim/HistoryClaimComponent.vue";
import UtilizationInsight from "@/components/insight/UtilizationInsightComponent.vue";
import { vetBypass } from "@/laravel/config";
import M from "materialize-css";

export default {
  name: "claimView",
  props: ["clusterId"],
  components: { SubHeading, LoadingDiv, VetClaim, BenefitsDiv, HistoryClaim, UtilizationInsight },
  setup(props) {
    const response = ref(null);
    const error = ref(null);
    const isReady = ref(false);
    const isSubmitting = ref(false);
    const isDeletingRelated = ref(false);
    const isLoading = ref(true);
    const currUrl = ref(null);
    const encounterId = ref(0);
    const cluster = ref(null);
    const clusterProvider = ref(null);
    const clusterPeriod = ref(null);
    const clusterScheme = ref(null);
    const clusterRef = ref(null);
    const clusterType = ref(null);
    const clusterStatus = ref(null);
    const clusterPayment = ref(null);
    const clusterRepayment = ref(null);
    const relatedClusters = ref([]);
    const clusterUserType = ref(null);
    const clusterTAT = ref(null);
    const clusterTATCounting = ref(false);
    const currState = ref("v"); // v=vet, d=display, s=status
    const complete = ref(false);
    const pendingSpecialAction = ref(null);
    const pendingRelatedDelete = ref(false);
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);
    const tabsState = ref([]);
    const claimDetailState = ref("review");
    const claimDetailTabs = ref([
      { id: 1, title: "Review", state: "review" },
      { id: 2, title: "Benefits", state: "benefits" },
      { id: 3, title: "History", state: "history" },
      { id: 4, title: "Utilization", state: "utilization" },
    ]);

    // vue router
    const router = useRouter();
    const { routeToTab } = useModuleNavigation();

    // store
    const store = useStore();

    // start
    onMounted(() => {
      // page name
      document.title = "View Claims | EasyHMO";
    });

    // menu
    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Claims", title: "All", icon: "arrow_back", url: "claim", hash: null });
      tabs.value.push({ id: 2, name: "Claims", title: "Vet", icon: null, url: null, hash: null });
      tabs.value.push({ id: 3, name: "Claims", title: "Add Claims", icon: "add_circle", url: "claimAdd", hash: null });

      // set default
      currTab.value = tabs.value.find((x) => x.title.includes("Vet"));
      // set home
      home.value = tabs.value.find((x) => x.id === 1);
    });

    // start
    onMounted(() => {
      // load tab
      tabsState.value.push({ id: 1, title: "View All Pages", state: "d" });
      tabsState.value.push({ id: 2, title: "Show Status", state: "s" });
    });

    // check for cluster status
    watchEffect(() => {
      if (clusterStatus.value !== "p") {
        tabsState.value = tabsState.value.filter((x) => x.state !== "v");
      } else if (!tabsState.value.find((x) => x.state === "v")) {
        tabsState.value.push({ id: 3, title: "Vet Claims", state: "v" });
      }
    });

    // handle curr status
    const handleActive = (tab) => {
      routeToTab(tab, { replace: !tab?.url });
    };

    // get title
    const getTitle = (val) => {
      if (val.status == "p") {
        return "Un-Vetted Claim";
      } else if (val.status == "d") {
        return "Completed (Rejected)";
      } else {
        return "Completed";
      }
    };

    //from functions
    const capWords = (val) => {
      return capWordsFx(val);
    };

    // total calculations (gross sent)
    const grossTotalSent = (encounters) => {
      return grossTotalSentFx(encounters);
    };

    // total calculations (gross approved)
    const grossTotalApproved = (encounters, db) => {
      return grossTotalApprovedFx(encounters, db);
    };

    // total calculations (sent)
    const totalSent = (encounter) => {
      return totalSentFx(encounter);
    };

    // total calculations (approved)
    const totalApproved = (encounter) => {
      return totalApprovedFx(encounter);
    };

    const rawTotalSent = (encounter) => {
      return (encounter?.claims || []).reduce((total, claim) => {
        return total + Number(claim?.sent_price || 0);
      }, 0);
    };

    const rawTotalApproved = (encounter) => {
      if (encounter?.status === "d") {
        return 0;
      }

      return (encounter?.claims || []).reduce((total, claim) => {
        if (claim?.status === "a" && claim?.approved_price) {
          return total + Number(claim.approved_price);
        }

        return total;
      }, 0);
    };

    const claimVettedAmountClass = (encounter) => {
      if (!encounter || encounter.status === "p" || encounter.status === "d") {
        return "";
      }

      const sent = rawTotalSent(encounter);
      const approved = rawTotalApproved(encounter);

      if (approved < sent) {
        return "is-lower-than-sent";
      }

      if (approved > sent) {
        return "is-higher-than-sent";
      }

      return "";
    };

    // payment status
    const paymentStatus = (cluster) => {
      return paymentStatusFx(cluster);
    };

    const normalizeDate = (value) => {
      if (!value) {
        return null;
      }

      const date = new Date(String(value).replace(" ", "T"));
      return Number.isNaN(date.getTime()) ? null : date;
    };

    const estimatedSubmittedDate = (monthYear) => {
      if (!monthYear) {
        return null;
      }

      const [month, year] = String(monthYear).split("_").map(Number);
      if (!month || !year) {
        return null;
      }

      return new Date(year, month, 1);
    };

    const paidDate = (payment) => {
      if (!payment || !["c", "z"].includes(payment.stage)) {
        return null;
      }

      return normalizeDate(payment.payment_date || payment.paid_date);
    };

    const updateClusterTAT = (clusterValue) => {
      const start =
        normalizeDate(clusterValue?.submitted_date) ||
        estimatedSubmittedDate(clusterValue?.month_year) ||
        normalizeDate(clusterValue?.created_at) ||
        new Date();

      const end = paidDate(clusterValue?.payment) || paidDate(clusterValue?.repayment);
      const endDate = end || new Date();
      const diff = Math.floor((endDate - start) / (1000 * 60 * 60 * 24));

      clusterTAT.value = Math.max(diff, 0);
      clusterTATCounting.value = !end;
    };

    // get function
    const getItemsFromApi = (table, id) => {
      (async () => {
        const { res, err } = await getItemsOfItems(table, id);

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = err.value;
        }

        // page is ready, loading is complete
        isReady.value = true;
        isLoading.value = false;
      })();
    };

    // starter
    const starter = () => {
      if (props.clusterId) {
        (async () => {
          const { res } = await getItemsOfItems("cluster", props.clusterId);

          if (res.value) {
            // cluster period
            const { fx: monthYear } = toMonthYear(res.value.data.month_year);
            cluster.value = res.value.data; // all
            clusterPeriod.value = monthYear.value;
            clusterProvider.value = res.value.data.provider.name;
            clusterScheme.value = res.value.data.scheme.name;
            clusterRef.value = res.value.data.ref;
            clusterType.value = res.value.data.type;
            clusterStatus.value = res.value.data.status;
            clusterPayment.value = res.value.data.payment;
            clusterRepayment.value = res.value.data.repayment;
            relatedClusters.value = res.value.data.related_clusters || [];
            clusterUserType.value = res.value.data.user?.type;

            updateClusterTAT(res.value.data);

            // reset status
            if (clusterStatus.value === "v" || clusterStatus.value === "r") {
              currState.value = "s";
            }

            // send q and d away
            if (clusterStatus.value === "q" || clusterStatus.value === "d") {
              // route back home
              router.push({ name: "claim" });
            }
          } else {
            // cluster Error
            error.value = "(Error) No Claims Encounter Found";
          }
        })();
      }
    };

    // clusterPeriod getter
    onMounted(() => {
      starter();
    });

    // more
    onUpdated(() => {
      const elems = document.querySelectorAll(".dropdown-trigger");
      M.Dropdown.init(elems, {});
    });

    // starter
    onMounted(() => {
      // set current Url
      currUrl.value = "encounter/cluster";
      // get items from function
      getItemsFromApi(currUrl.value, props.clusterId);
    });

    // Send Back claims
    const handleReInput = async () => {
      // start
      isReady.value = false;

      // update
      const newVal = { status: "q" };

      // put update
      const { res } = await putItem("cluster/return", props.clusterId, newVal);

      if (res.value) {
        // route back home
        router.push({ name: "claim" });
      } else {
        // close out
        // restart page
        starter();
        // start
        isReady.value = true;
      }
    };

    // switch page
    const scrollToVettingCard = () => {
      const target = document.querySelector(".claim-dossier-metrics") || document.querySelector(".claim-mode-strip") || document.querySelector(".claim-vetting-surface");
      if (!target) {
        return;
      }

      const top = target.getBoundingClientRect().top + window.scrollY - 18;
      window.scrollTo({ behavior: "smooth", top: Math.max(top, 0) });
    };

    const clearClaimViewHash = () => {
      if (!window.location.hash) {
        return;
      }

      window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}`);
    };

    const handleStart = async (encounter) => {
      // start spec laoding
      if (props.clusterId) {
        clearClaimViewHash();
        // re-assign
        encounterId.value = encounter.id;
        // reset detail tab
        claimDetailState.value = "review";
        // switch to vetting
        currState.value = "v";
        await nextTick();
        window.requestAnimationFrame(scrollToVettingCard);
      }
    };

    const prepareClusterForInput = (clusterValue) => {
      const nextCluster = { ...clusterValue };
      const monthYear = String(nextCluster.month_year || "").split("_");

      nextCluster.month = {
        search: Number(monthYear[0]),
        name: getFullMonthName(Number(monthYear[0]) - 1),
      };
      nextCluster.year = {
        search: Number(monthYear[1]),
        name: monthYear[1],
      };
      nextCluster.encounters = nextCluster.encounters || [];

      return nextCluster;
    };

    const prepareRelatedClusterForInput = (related) => {
      return prepareClusterForInput({
        ...related,
        provider: cluster.value?.provider,
        scheme: cluster.value?.scheme,
        encounters: related.encounters || [],
      });
    };

    const relatedLabel = (related) => {
      if (related.type === "original" && related.ref === "a") {
        return "Main";
      }

      return `${capWords(related.type)} ${String(related.ref || "").toUpperCase()}`;
    };

    const handleRelatedClaim = (related) => {
      if (Number(related.id) === Number(cluster.value?.id)) {
        return;
      }

      if (related.needsCompletion) {
        (async () => {
          const { res } = await getItemsOfItems("cluster", related.id);
          const relatedCluster = res.value?.data || related;
          store.commit("loadCluster", prepareRelatedClusterForInput(relatedCluster));
          router.push({ name: "claimAdd" });
        })();
        return;
      }

      store.commit("loadCluster", null);
      router.push({ name: "claimView", params: { clusterId: related.id } });
    };

    const handleSpecialActionOpen = (type) => {
      if (!canCreateRelatedCluster.value || isSpecialActionTypeBlocked(type) || isSubmitting.value) {
        return;
      }

      pendingSpecialAction.value = type;
    };

    const handleSpecialActionClose = () => {
      if (isSubmitting.value) {
        return;
      }

      pendingSpecialAction.value = null;
    };

    const handleSpecialActionConfirm = () => {
      if (!pendingSpecialAction.value) {
        return;
      }

      handleCreateRelated(pendingSpecialAction.value);
    };

    const handleCreateRelated = async (type) => {
      if (!canCreateRelatedCluster.value || isSubmitting.value) {
        return;
      }

      isSubmitting.value = true;
      const { res, err } = await postItem(`cluster/${props.clusterId}/related`, {
        type,
        user_id: store.state.user.id,
        slug: store.state.user.slug,
      });

      if (res.value?.data) {
        M.toast({
          html: `${capWords(type)} claims created <i class='material-icons'>check_circle</i>`,
          classes: "green",
          displayLength: 4000,
        });

        if (type === "supplementary") {
          store.commit("loadCluster", prepareClusterForInput(res.value.data));
          router.push({ name: "claimAdd" });
        } else {
          store.commit("loadCluster", null);
          router.push({ name: "claimView", params: { clusterId: res.value.data.id } });
        }
      } else {
        M.toast({
          html: `${err.value || "Unable to create related claims."}`,
          classes: "red",
          displayLength: 5000,
        });
      }

      isSubmitting.value = false;
      pendingSpecialAction.value = null;
    };

    const handleRelatedDeleteOpen = () => {
      if (!showRelatedDeleteAction.value || isDeletingRelated.value) {
        return;
      }

      pendingRelatedDelete.value = true;
    };

    const handleRelatedDeleteClose = () => {
      if (isDeletingRelated.value) {
        return;
      }

      pendingRelatedDelete.value = false;
    };

    const handleRelatedDeleteConfirm = async () => {
      if (!showRelatedDeleteAction.value || isDeletingRelated.value) {
        return;
      }

      isDeletingRelated.value = true;
      const { res, err } = await putItem(`cluster/${props.clusterId}/related`, "delete", {});

      if (res.value?.data) {
        const deletedCluster = res.value.data;
        cluster.value = deletedCluster;
        response.value = deletedCluster.encounters || [];
        clusterStatus.value = deletedCluster.status;
        clusterPayment.value = deletedCluster.payment;
        clusterRepayment.value = deletedCluster.repayment;
        relatedClusters.value = deletedCluster.related_clusters || relatedClusters.value;
        currState.value = "s";
        updateClusterTAT(deletedCluster);

        M.toast({
          html: "Related claim batch deleted <i class='material-icons'>check_circle</i>",
          classes: "green",
          displayLength: 5000,
        });
      } else {
        M.toast({
          html: `${err.value || "Unable to delete related claim batch."}`,
          classes: "red",
          displayLength: 5000,
        });
      }

      isDeletingRelated.value = false;
      pendingRelatedDelete.value = false;
    };

    const handleContinueVetting = async () => {
      const nextEncounter = encounters.value.find((x) => x.status === "p") || encounters.value.find((x) => x.status !== "d") || encounters.value[0];

      if (nextEncounter) {
        await handleStart(nextEncounter);
      }
    };

    // review
    const handleReview = (val) => {
      clearClaimViewHash();
      claimDetailState.value = "review";
      // switch to display
      if (val == "v") {
        currState.value = "v";
      } else if (val == "d") {
        currState.value = "d";
      } else if (val == "s") {
        currState.value = "s";
      }
    };

    const handleClaimDetailState = (val) => {
      claimDetailState.value = val;
    };

    // vetted
    const handleVetted = async (val) => {
      // switch to display
      currState.value = "d";

      // reload // val.status == 'success'
      if (val.status == "success" && clusterStatus.value == "p") {
        //after checking emits, checking cluster status
        // setup cluster
        const dbEntry = true;
        const payment_final = grossTotalApproved(val.value, dbEntry);
        const newVal = {
          secondary_user_id: store.state.user.id,
          level: store.state.user.level,
          payment_final: payment_final,
          status: "v",
          vetBypass: vetBypass, // check supervisor/HOD vetting bypass
        };

        // put update
        const { res, err } = await putItem("cluster", props.clusterId, newVal);

        if (res.value) {
          // update
          response.value = val.value;
          // update cluster from .cluster object (outside the ususal .data)
          clusterStatus.value = res.value.cluster.status;
          // update cluster status
          cluster.value.status = res.value.cluster.status;
          // notify (Toast)
          M.toast({
            html: "Claims Vetting Completed <i class='material-icons'>check_circle</i>", // Toast content
            classes: "green", // Add custom classes // green rounded
            displayLength: 5000, // Duration in milliseconds
            inDuration: 300, // Transition in duration
            outDuration: 300, // Transition out duration
          });
        } else {
          error.value = err.value;
        }
      }
    };

    // download
    const handleDownload = () => {
      const data = [];

      // total
      encounters.value.forEach((x, y) => {
        // detail
        x.claims.forEach((z, j) => {
          if (j === 0) {
            // first
            data.push({
              No: y + 1,
              Name: x.name,
              ID: x.enrollee_number,
              Encounter: x.encounter_date,
              End: x.encounter_date_end,
              Diagnosis: x.diagnosis,
              Item: z.item,
              sent_qty: z.sent_qty,
              appr_qty: x.status == "d" || z.status == "d" ? 0 : z.approved_qty,
              Sent: z.sent_price,
              Approved: x.status == "d" || z.status == "d" ? 0 : z.approved_price,
              Comment: z.comment,
            });
          } else {
            // others
            data.push({
              No: "",
              Name: "",
              ID: "",
              Encounter: "",
              End: "",
              Diagnosis: "",
              Item: z.item,
              sent_qty: z.sent_qty,
              appr_qty: x.status == "d" || z.status == "d" ? 0 : z.approved_qty,
              Sent: z.sent_price,
              Approved: x.status == "d" || z.status == "d" ? 0 : z.approved_price,
              Comment: z.comment,
            });
          }
        });

        // total
        data.push({
          No: "",
          Name: "",
          ID: "",
          Encounter: "",
          End: "",
          Diagnosis: "",
          Item: "",
          sent_qty: "",
          appr_qty: "",
          Sent: totalSent(x),
          Approved: totalApproved(x),
          Comment: "",
        });

        // space
        data.push({});
      });

      // space
      data.push({});

      // final
      data.push({
        No: "",
        Name: "",
        ID: "",
        Encounter: "",
        End: "",
        Diagnosis: "",
        Item: "",
        sent_qty: "",
        appr_qty: "Final",
        Sent: grossTotalSent(encounters.value),
        Approved: grossTotalApproved(encounters.value),
        Comment: "",
      });

      // fire function
      return downloadExcelFx(data, `${clusterProvider.value}_${clusterScheme.value}_${clusterPeriod.value} Claims`);
    };

    // monitor complete
    watchEffect(() => {
      if (response.value) {
        // start with
        complete.value = true;
        // update
        response.value.forEach((x) => {
          if (x.status == "p") {
            // set to false
            complete.value = false;
          }
        });
      }
    });

    // output results
    const encounters = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    const displayTabsState = computed(() => {
      const seen = new Set();
      return tabsState.value.filter((tab) => {
        if (seen.has(tab.state)) {
          return false;
        }
        seen.add(tab.state);
        return true;
      });
    });

    const activeVettingEncounter = computed(() => {
      if (!encounters.value.length) {
        return null;
      }

      return (
        encounters.value.find((x) => Number(x.id) === Number(encounterId.value)) ||
        encounters.value.find((x) => x.status === "p") ||
        encounters.value[0]
      );
    });

    const activeVettingBenefitPlanId = computed(() => {
      const encounter = activeVettingEncounter.value;
      return encounter?.plan_id || encounter?.plan?.id || encounter?.principal?.plan_id || encounter?.principal?.plan?.id || null;
    });

    const clusterStatusLabel = computed(() => {
      if (clusterStatus.value === "p") {
        return "Unvetted";
      }
      if (clusterStatus.value === "v" || clusterStatus.value === "r") {
        return "Vetted";
      }
      return "Review";
    });

    const clusterStatusClass = computed(() => {
      if (clusterStatus.value === "p") {
        return "is-unvetted";
      }
      if (clusterStatus.value === "v" || clusterStatus.value === "r") {
        return "is-vetted";
      }
      return "is-neutral";
    });

    const clusterPaymentStatus = computed(() => {
      return cluster.value ? paymentStatus(cluster.value) : "Payment status pending";
    });

    const relatedClaimLinks = computed(() => {
      const family = relatedClusters.value || [];
      const hasRelated = family.some((related) => !(related.ref === "a" && related.type === "original"));

      if (!hasRelated) {
        return [];
      }

      return family.map((related) => ({
        ...related,
        label: relatedLabel(related),
        needsCompletion: related.type === "supplementary" && related.status === "q",
        needsReview: related.type !== "original" && related.status === "p",
      }));
    });

    const clusterRelatedLabel = computed(() => {
      if (!cluster.value || clusterRef.value === "a" || clusterType.value === "original") {
        return null;
      }

      return `${capWords(clusterType.value)} ${String(clusterRef.value || "").toUpperCase()}`;
    });

    const canCreateRelatedCluster = computed(() => {
      return (
        cluster.value &&
        clusterRef.value === "a" &&
        clusterType.value === "original" &&
        Number(cluster.value.payment_id || 0) > 0
      );
    });

    const showSpecialActions = computed(() => {
      return cluster.value && clusterRef.value === "a" && clusterType.value === "original";
    });

    const showReturnClaims = computed(() => {
      return clusterStatus.value === "p" && clusterType.value !== "reconciliation" && clusterUserType.value && store.state.user.level === 5;
    });

    const showRelatedDeleteAction = computed(() => {
      return (
        cluster.value &&
        clusterRef.value !== "a" &&
        ["supplementary", "reconciliation"].includes(clusterType.value) &&
        clusterStatus.value === "p" &&
        Number(cluster.value.payment_id || 0) === 0
      );
    });

    const activeRelatedByType = (type) => {
      return (relatedClusters.value || []).find((related) => {
        return related.ref !== "a" && related.type === type && Number(related.payment_id || 0) === 0;
      });
    };

    const isSpecialActionTypeBlocked = (type) => {
      return Boolean(activeRelatedByType(type));
    };

    const specialActionTypeReason = (type) => {
      if (!isSpecialActionTypeBlocked(type)) {
        return null;
      }

      return `A ${type} claim is already active for this claim period. Complete and payment-batch it before creating another.`;
    };

    const specialActionTypeLabel = (type) => {
      const base = `Create ${capWords(type)} Claim`;

      if (!isSpecialActionTypeBlocked(type)) {
        return base;
      }

      return `${base} (Active)`;
    };

    const specialActionReason = computed(() => {
      if (!cluster.value) {
        return "Special actions are not available yet.";
      }

      if (clusterRef.value !== "a" || clusterType.value !== "original") {
        return "Special actions can only be created from the original claim batch.";
      }

      if (Number(cluster.value.payment_id || 0) <= 0) {
        return "Available after the claim has passed review and has been batched for payment.";
      }

      return null;
    });

    const specialActionModal = computed(() => {
      if (pendingSpecialAction.value === "reconciliation") {
        return {
          title: "Create Reconciliation Claim?",
          message: "This will create a new reconciliation batch linked to this original claim period. Use this when the claim has already moved into payment processing but needs a correction or follow-up review.",
          next: "After creation, you will remain in Claim View and the new reconciliation batch will open for review.",
          confirm: "Create Reconciliation",
        };
      }

      return {
        title: "Create Supplementary Claim?",
        message: "This will create a new supplementary batch linked to this original claim period. Use this when missing claims were found after the original claim moved into payment processing.",
        next: "After creation, you will be taken to Add Claims so new enrollees, encounters, and claim items can be captured.",
        confirm: "Create Supplementary",
      };
    });

    watch(
      () => props.clusterId,
      (clusterId) => {
        if (!clusterId) {
          return;
        }

        currUrl.value = "encounter/cluster";
        starter();
        getItemsFromApi(currUrl.value, clusterId);
      }
    );

    return {
      user: store.state.user,
      handleActive,
      home,
      currTab,
      tabs,
      tabsState,
      displayTabsState,
      claimDetailState,
      claimDetailTabs,
      currState,
      capWords,
      handleDownload,
      handleReInput,
      handleSpecialActionOpen,
      handleSpecialActionClose,
      handleSpecialActionConfirm,
      handleCreateRelated,
      handleRelatedDeleteOpen,
      handleRelatedDeleteClose,
      handleRelatedDeleteConfirm,
      handleRelatedClaim,
      handleStart,
      handleContinueVetting,
      handleReview,
      handleClaimDetailState,
      handleVetted,
      grossTotalSent,
      grossTotalApproved,
      totalApproved,
      totalSent,
      claimVettedAmountClass,
      paymentStatus,
      getTitle,
      isReady,
      isLoading,
      isSubmitting,
      isDeletingRelated,
      pendingSpecialAction,
      pendingRelatedDelete,
      specialActionModal,
      error,
      encounterId,
      cluster,
      clusterProvider,
      clusterPeriod,
      clusterScheme,
      clusterRef,
      clusterType,
      clusterRelatedLabel,
      clusterStatus,
      clusterStatusLabel,
      clusterStatusClass,
      clusterPayment,
      clusterRepayment,
      relatedClusters,
      relatedClaimLinks,
      clusterPaymentStatus,
      canCreateRelatedCluster,
      showSpecialActions,
      showReturnClaims,
      showRelatedDeleteAction,
      isSpecialActionTypeBlocked,
      specialActionTypeReason,
      specialActionTypeLabel,
      specialActionReason,
      clusterUserType,
      clusterTAT,
      clusterTATCounting,
      complete,
      encounters,
      activeVettingBenefitPlanId,
      activeVettingEncounter,
    };
  },
};
</script>

<style scoped>
.claim-review-workbench {
  color: #263238;
  padding-top: 0;
  padding-bottom: 48px;
  width: 100%;
}

.claim-special-confirm-overlay {
  align-items: center;
  background: rgba(38, 50, 56, 0.48);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 24px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1800;
}

.claim-special-confirm-modal {
  background: #ffffff;
  border: 1px solid #d7e1e5;
  border-radius: 6px;
  box-shadow: 0 24px 60px rgba(38, 50, 56, 0.28);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 96px);
  max-width: 720px;
  width: min(720px, 100%);
}

.claim-special-confirm-modal.is-danger {
  border-color: #ffcdd2;
}

.claim-special-confirm-content {
  overflow-y: auto;
  padding: 34px 36px 22px;
}

.claim-special-confirm-content h4 {
  color: #263238;
  font-size: 2rem;
  font-weight: 500;
  letter-spacing: 0;
  margin: 6px 0 18px;
}

.claim-special-confirm-content p {
  color: #263238;
  font-size: 1rem;
  line-height: 1.55;
  margin: 0;
}

.claim-special-confirm-next {
  background: #eef7f6;
  border: 1px solid #c8e4df;
  border-radius: 6px;
  color: #00695c !important;
  font-weight: 700;
  margin-top: 16px !important;
  padding: 12px 14px;
}

.claim-special-confirm-next.is-danger {
  background: #fff5f5;
  border-color: #ffcdd2;
  color: #b71c1c !important;
}

.claim-special-confirm-footer {
  align-items: center;
  border-top: 1px solid #e1e8eb;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 12px 18px;
}

.claim-special-confirm-btn {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 5px;
  color: #37474f;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 800;
  min-height: 36px;
  padding: 0 14px;
  text-transform: uppercase;
}

.claim-special-confirm-btn:hover {
  background: #eef3f6;
}

.claim-special-confirm-btn.is-confirm {
  color: #00695c;
}

.claim-special-confirm-btn.is-danger {
  background: #b71c1c;
  color: #ffffff;
}

.claim-special-confirm-btn.is-danger:hover {
  background: #8f1515;
}

.claim-special-confirm-btn:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.claim-super-card,
.claim-dossier-card,
.claim-dossier-metrics,
.claim-mode-strip,
.claim-secondary-body-frame,
.claim-vetting-surface,
.claim-encounter-table,
.encounter-review-row,
.claim-total-strip,
.claim-status-panel,
.claim-review-workbench :deep(.vetting-soft-core),
.claim-review-workbench :deep(.vetting-core-card),
.claim-review-workbench :deep(.vetting-member-card),
.claim-review-workbench :deep(.vetting-diagnosis-card),
.claim-review-workbench :deep(.vetting-claims-card),
.claim-review-workbench :deep(.vetting-edit-card) {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.claim-secondary-header {
  box-shadow: 0 20px 34px -32px rgba(38, 50, 56, 0.82);
  margin-bottom: 0;
  overflow: visible;
  position: relative;
}

.claim-secondary-body-frame {
  background: #f5f7f8;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
  box-sizing: border-box;
  margin-top: 14px;
  padding: 16px;
  position: relative;
}

.claim-dossier-card,
.claim-super-card,
.claim-dossier-metrics,
.claim-mode-strip,
.claim-vetting-surface,
.claim-encounter-table,
.encounter-review-row,
.claim-total-strip,
.claim-status-panel {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: none;
}

.claim-super-card {
  align-items: center;
  background: #fff8e1;
  border-bottom: 0;
  border-radius: 6px 6px 0 0;
  box-shadow: 0 10px 20px -24px rgba(121, 85, 72, 0.55);
  display: flex;
  gap: 10px;
  min-height: 46px;
  padding: 10px 14px;
  position: relative;
  width: 100%;
  z-index: 3;
}

.claim-super-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.15;
}

.claim-dossier-card {
  align-items: flex-end;
  border-radius: 0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: 0 12px 20px -18px rgba(38, 50, 56, 0.55);
  display: flex;
  gap: 22px;
  justify-content: space-between;
  padding: 26px 34px;
  position: relative;
}

.claim-dossier-title label,
.status-title label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.secondary-page-back {
  align-items: center;
  background: #00897b;
  border: 1px solid #00796b;
  border-radius: 5px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 24px;
  justify-content: center;
  margin: 0;
  padding: 0;
  width: 24px;
}

.secondary-page-back:hover {
  background: #00695c;
  border-color: #00695c;
}

.secondary-page-back i {
  color: #ffffff;
  font-size: 16px;
  line-height: 16px;
}

.secondary-parent-link {
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #4e342e;
  cursor: pointer;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 800;
  margin: 0;
  padding: 0;
  text-align: left;
  text-transform: uppercase;
  transition: background-color 0.18s ease, color 0.18s ease;
}

.secondary-parent-link:hover {
  color: #00897b;
}

.claim-super-copy small {
  color: #6d4c41;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: none;
}

.claim-dossier-title h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
  overflow-wrap: anywhere;
}

.claim-dossier-title p,
.status-title p {
  color: #607d8b;
  line-height: 1.55;
  margin: 0;
  max-width: 760px;
}

.claim-dossier-context {
  align-items: flex-end;
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  max-width: 430px;
}

.context-pill {
  background: #fafafa;
  border: 1px solid #e1e8eb;
  border-radius: 999px;
  color: #455a64;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.2;
  padding: 8px 11px;
}

.context-pill.is-unvetted {
  background: #fff4d6;
  border-color: #f1c75b;
  color: #795000;
}

.context-pill.is-vetted {
  background: #e0f2f1;
  border-color: #80cbc4;
  color: #00695c;
}

.context-pill.is-payment {
  background: #eceff1;
  color: #263238;
}

.context-pill.is-related {
  text-transform: uppercase;
}

.context-pill.is-related.is-supplementary {
  background: #e8f5e9;
  border-color: #a5d6a7;
  color: #2e7d32;
}

.context-pill.is-related.is-reconciliation {
  background: #e3f2fd;
  border-color: #90caf9;
  color: #1565c0;
}

.claim-dossier-metrics {
  border-color: #c4d3d9;
  border-top: 0;
  border-radius: 0;
  box-shadow: none;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 0;
}

.metric-cell {
  border-left: 1px solid #e1e8eb;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-height: 76px;
  padding: 17px 22px;
}

.metric-cell:first-child {
  border-left: 0;
}

.metric-cell span,
.status-grid span {
  color: #607d8b;
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
}

.metric-cell strong,
.status-grid strong {
  color: #263238;
  font-size: 1.02rem;
  font-weight: 800;
  line-height: 1.35;
}

.metric-cell strong.metric-relaxed-value {
  color: #37474f;
  font-size: 0.98rem;
  font-weight: 650;
}

.tat-counting-chip {
  align-items: center;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 999px;
  color: #795000 !important;
  display: inline-flex;
  max-width: max-content;
  min-height: 30px;
  padding: 0 11px;
}

.tat-counting-text {
  margin-left: 4px;
}

.metric-approved-value {
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  line-height: 1.2;
  max-width: max-content;
  min-height: 32px;
  padding: 0 11px;
}

.metric-approved-value.is-ready {
  background: #e6fff5;
  border: 1px solid #5ee0b4;
  color: #008a5b;
}

.metric-approved-value.is-pending {
  background: #fff8e1;
  border: 1px solid #ffe082;
  color: #795548;
}

.metric-action-cell {
  align-items: flex-end;
  justify-content: center;
}

.download-action,
.continue-vetting,
.return-menu-trigger {
  align-items: center;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  color: #263238;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.86rem;
  font-weight: 700;
  gap: 8px;
  min-height: 38px;
  padding: 0 13px;
}

.download-action i,
.continue-vetting i {
  color: #00897b;
  font-size: 19px;
}

.special-actions-wrap {
  display: inline-flex;
}

.return-menu-trigger.disabled {
  background: #f5f5f5;
  color: #90a4ae;
  cursor: not-allowed;
  opacity: 0.75;
  pointer-events: none;
}

.return-menu-trigger.disabled i {
  color: #90a4ae;
}

.claim-mode-strip {
  align-items: center;
  background: #ffffff;
  border-color: #c4d3d9;
  border-top: 0;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 0;
  padding: 10px 16px;
}

.claim-related-strip {
  align-items: center;
  background: #fbfcfc;
  border: 1px solid #c4d3d9;
  border-top: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 16px;
}

.claim-related-link {
  align-items: center;
  background: #ffffff;
  border: 1px solid #d7e1e5;
  border-radius: 6px;
  color: #37474f;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 800;
  gap: 8px;
  min-height: 34px;
  padding: 0 11px;
}

.claim-related-link:hover,
.claim-related-link.is-active {
  background: #263238;
  border-color: #263238;
  color: #ffffff;
}

.claim-related-link.is-draft,
.claim-related-link.is-pending {
  border-color: #ffe082;
}

.claim-related-link small {
  background: #fff8e1;
  border-radius: 999px;
  color: #795000;
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 1;
  padding: 5px 7px;
  text-transform: uppercase;
}

.claim-related-link.is-pending small {
  background: #eceff1;
  color: #455a64;
}

.claim-related-link:hover small,
.claim-related-link.is-active small {
  background: rgba(255, 255, 255, 0.18);
  color: #ffffff;
}

.claim-mode-actions {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
  margin-left: auto;
  padding: 5px;
}

.claim-mode-option {
  background: #f8fafb;
  border: 1px solid #c4d3d9;
  border-radius: 5px;
  box-shadow: 0 8px 16px -18px rgba(38, 50, 56, 0.8);
  color: #263238;
  cursor: pointer;
  flex: 0 0 auto;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0;
  min-height: 38px;
  padding: 0 16px;
  text-align: center;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease;
}

.claim-mode-option:first-child {
  border-left: 1px solid #c4d3d9;
}

.claim-mode-option:hover {
  background: #eef7f7;
  border-color: #80cbc4;
  box-shadow: 0 10px 18px -18px rgba(0, 105, 92, 0.55);
  color: #00695c;
}

.claim-mode-option.is-active {
  background: #263238;
  border-color: #263238;
  box-shadow: 0 10px 18px -14px rgba(38, 50, 56, 0.9);
  color: #ffffff;
}

.claim-mode-close {
  align-items: center;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 5px;
  color: #c62828;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 38px;
  justify-content: center;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease;
  width: 42px;
}

.claim-mode-close:hover,
.claim-mode-close:focus {
  background: #fff7f7;
  border-color: #ef9a9a;
  box-shadow: 0 10px 18px -18px rgba(198, 40, 40, 0.55);
  color: #b71c1c;
}

.claim-mode-close i {
  font-size: 20px;
  line-height: 1;
}

.claim-vetting-surface {
  border-radius: 6px;
  padding: 16px;
}

.claim-vetting-tertiary {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 6px 6px 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  justify-content: center;
  margin: 0 0 -1px;
  padding: 0 14px;
  position: relative;
  z-index: 1;
}

.claim-vetting-tertiary-link {
  align-items: center;
  background: transparent;
  border: 0;
  border-bottom: 3px solid transparent;
  border-radius: 0;
  color: #607d8b;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 800;
  justify-content: center;
  letter-spacing: 0;
  line-height: 1.15;
  min-height: 46px;
  padding: 0 22px;
  text-transform: uppercase;
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.claim-vetting-tertiary-link:hover {
  background: #f4f7f8;
  color: #263238;
}

.claim-vetting-tertiary-link.is-active {
  border-bottom-color: #263238;
  color: #263238;
}

.claim-vetting-tertiary-panel {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 0 0 6px 6px;
  box-shadow: 0 18px 32px -33px rgba(38, 50, 56, 0.72);
  padding: 18px;
}

.claim-vetting-tertiary-panel :deep(.card),
.claim-vetting-tertiary-panel :deep(.card-panel),
.claim-vetting-tertiary-panel :deep(nav) {
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  box-shadow: none;
}

.claim-vetting-tertiary-panel :deep(nav) {
  overflow: hidden;
}

.claim-vetting-tertiary-panel :deep(.card-panel) {
  margin-bottom: 10px;
}

.claim-vetting-empty-note {
  align-items: center;
  background: #f8fafb;
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  color: #607d8b;
  display: flex;
  gap: 8px;
  justify-content: center;
  min-height: 90px;
  padding: 18px;
}

.claim-vetting-empty-note i {
  color: #00897b;
  font-size: 20px;
}

.claim-encounter-table {
  background: #ffffff;
  border: 1px solid #b8ccd4;
  border-radius: 6px;
  box-shadow: 0 0 0 6px #f6fafb, 0 0 0 7px #c9d8de, 0 18px 32px rgba(38, 50, 56, 0.08);
  font-family: Roboto, Arial, sans-serif;
  overflow: hidden;
}

.claim-encounter-table.is-complete {
  background: #ffffff;
}

.claim-encounter-table.is-complete .encounter-review-row {
  background: #ffffff;
}

.claim-encounter-table.is-complete .encounter-review-row:nth-child(even) {
  background: #ffffff;
}

.claim-encounter-table.is-complete .claim-total-strip {
  background: #fffaf0;
}

.claim-encounter-table table {
  color: #263238;
  font-family: inherit;
  margin: 0;
  table-layout: fixed;
}

.claim-encounter-col-index {
  width: 7%;
}

.claim-encounter-col-member {
  width: 24%;
}

.claim-encounter-col-diagnosis {
  width: 25%;
}

.claim-encounter-col-sent,
.claim-encounter-col-vetted {
  width: 17%;
}

.claim-encounter-col-status {
  width: 10%;
}

.claim-encounter-table thead {
  background: #f8fafb;
  color: #546e7a;
  font-size: 0.78rem;
  text-transform: uppercase;
}

.claim-encounter-table th {
  border-bottom: 1px solid #d7e1e5;
  color: #546e7a;
  font-family: inherit;
  font-weight: 800;
  padding: 14px 16px;
}

.claim-encounter-table th small {
  color: #546e7a;
  font-size: 0.72rem;
  font-weight: 800;
}

.claim-encounter-table td {
  border-bottom: 1px solid #e1e8eb;
  color: #263238;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 400;
  padding: 15px 16px;
  vertical-align: middle;
}

.encounter-review-row {
  background: #ffffff;
  cursor: pointer;
  transition: background-color 0.18s ease, box-shadow 0.18s ease;
}

.encounter-review-row:hover {
  background: #f8fafb;
  box-shadow: inset 3px 0 0 #80cbc4;
}

.encounter-review-row.is-pending {
  background: #fffdf7;
}

.encounter-review-row.is-deleted {
  background: #fafafa;
}

.encounter-index {
  color: #455a64;
  font-weight: 700;
  width: 54px;
}

.encounter-member,
.encounter-diagnosis,
.amount-cell,
.encounter-status {
  min-width: 0;
}

.encounter-member {
  color: #263238;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.4;
}

.encounter-member small {
  color: #263238;
  font-size: 0.84rem;
}

.encounter-diagnosis {
  color: #263238;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.35;
}

.amount-cell {
  color: #263238;
  font-size: 1rem;
  font-variant-numeric: tabular-nums;
  font-weight: 400;
  line-height: 1.35;
}

.amount-cell b {
  font-weight: 600;
}

.amount-cell b.is-lower-than-sent {
  color: #1f4e79;
}

.amount-cell b.is-higher-than-sent {
  color: #b94a48;
}

.amount-cell.is-approved {
  color: #263238;
}

.amount-cell.is-muted,
.claim-total-strip strong.is-muted {
  color: #90a4ae;
  font-weight: 700;
}

.encounter-status i {
  align-items: center;
  background: #f7fafb;
  border: 1px solid #e1e8eb;
  border-radius: 50%;
  color: #78909c;
  display: inline-flex;
  font-size: 17px;
  height: 28px;
  justify-content: center;
  line-height: 28px;
  width: 28px;
}

.encounter-status i.yellow-text {
  background: #fffaf0;
  border-color: #f2dfb3;
  color: #a8741a !important;
}

.encounter-status i.grey-text {
  color: #78909c !important;
}

.claim-total-strip {
  background: #fffaf0;
}

.claim-total-strip td {
  border-bottom: 0;
  font-weight: 800;
}

.claim-total-strip td:last-child {
  text-align: right;
  white-space: nowrap;
}

.claim-total-strip .claim-total-label {
  color: #263238;
  font-size: 1.14rem;
  font-weight: 800;
}

.claim-total-strip .claim-total-value {
  color: #263238;
  font-size: 1.18rem;
  font-weight: 700;
  letter-spacing: 0;
}

.claim-total-strip .claim-total-value.is-sent {
  color: #fb8c00;
}

.claim-total-strip .vetted-total-value.is-complete {
  color: #00796b;
  font-size: 1.18rem;
  font-weight: 700;
}

.continue-vetting {
  align-items: center;
  background: #263238;
  border-color: #ffffff;
  border-radius: 6px;
  border-style: solid;
  border-width: 1px;
  box-shadow: 0 9px 16px rgba(38, 50, 56, 0.14);
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 800;
  gap: 4px;
  height: 34px;
  line-height: 34px;
  max-width: 100%;
  outline: 1px solid #b8ccd4;
  outline-offset: 4px;
  padding: 0 10px;
}

.continue-vetting i {
  color: #ffffff;
  font-size: 18px;
}

.continue-vetting.disabled {
  opacity: 0.45;
  pointer-events: none;
}

.claim-status-panel {
  border-color: #c4d3d9;
  padding: 28px 34px;
}

.status-title h6 {
  color: #263238;
  font-size: 1.25rem;
  font-weight: 800;
  margin: 0 0 8px;
}

.status-grid {
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 22px;
  overflow: hidden;
}

.status-grid div {
  background: #fafafa;
  border-left: 1px solid #e1e8eb;
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 18px;
}

.status-grid div:first-child {
  border-left: 0;
}

.status-approved-value,
.status-tat-value {
  align-items: center;
  color: #37474f !important;
  display: inline-flex;
  font-size: 0.98rem;
  font-weight: 650;
  gap: 6px;
  justify-content: flex-start;
  line-height: 1.25;
  margin-top: 2px;
  max-width: max-content;
}

.status-approved-value.is-ready,
.status-approved-value.is-pending {
  background: transparent;
  border: 0;
  color: #37474f !important;
}

.status-tat-value.is-counting {
  color: #37474f !important;
}

.status-grid-icon {
  color: #78909c;
  font-size: 18px;
  line-height: 1;
}

.status-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 18px;
}

.related-delete-action {
  align-items: center;
  background: #b71c1c;
  border: 1px solid #b71c1c;
  border-radius: 5px;
  box-shadow: 0 8px 18px rgba(183, 28, 28, 0.18);
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.86rem;
  font-weight: 800;
  gap: 10px;
  justify-content: center;
  min-height: 48px;
  min-width: 240px;
  padding: 0 20px;
  text-transform: uppercase;
}

.related-delete-action:hover {
  background: #8f1515;
  border-color: #8f1515;
}

.related-delete-action i {
  font-size: 22px;
}

.related-delete-action:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.return-menu-trigger {
  color: #00897b;
}

.return-menu-trigger i {
  font-size: 18px;
  margin-left: 4px;
}

.normal-th {
  font-weight: normal;
}

.disabledItem {
  pointer-events: none;
  opacity: 0.5;
}

.progress {
  margin-top: -15px;
}

.claim-secondary-body-frame .progress {
  margin: 0 0 14px;
}

.claim-secondary-body-frame :deep(.section) {
  padding-bottom: 0;
  padding-top: 0;
}

.claim-secondary-body-frame :deep(.card),
.claim-secondary-body-frame :deep(.card-panel) {
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  box-shadow: none;
}

.claim-secondary-body-frame :deep(.card) {
  overflow: hidden;
}

.claim-secondary-body-frame :deep(.grey.lighten-4),
.claim-secondary-body-frame :deep(.grey.lighten-5) {
  background-color: #f8fafb !important;
}

.claim-secondary-body-frame :deep(.teal.lighten-5) {
  background-color: #e8f6f6 !important;
}

.claim-secondary-body-frame :deep(.yellow.lighten-1) {
  background-color: #fff176 !important;
  border-radius: 4px;
}

.claim-secondary-body-frame :deep(.collection) {
  border-color: #d7e1e5;
  border-radius: 6px;
  overflow: hidden;
}

.claim-secondary-body-frame :deep(.collection-item) {
  border-bottom-color: #e1e8eb;
}

.claim-secondary-body-frame :deep(.btn),
.claim-secondary-body-frame :deep(.btn-large),
.claim-secondary-body-frame :deep(.chip) {
  letter-spacing: 0;
}

.pointable {
  cursor: default;
}

@media only screen and (max-width: 992px) {
  .claim-view-container {
    width: 96%;
  }

  .claim-dossier-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .claim-dossier-context {
    justify-content: flex-start;
    max-width: none;
    min-width: 0;
    width: 100%;
  }

  .claim-dossier-metrics,
  .status-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .metric-cell:nth-child(3) {
    border-left: 0;
    border-top: 1px solid #e1e8eb;
  }

  .metric-cell:nth-child(4) {
    border-top: 1px solid #e1e8eb;
  }

  .claim-mode-strip,
  .claim-related-strip {
    align-items: flex-start;
    flex-direction: column;
  }

  .claim-mode-actions {
    justify-content: flex-start;
    width: 100%;
  }

  .claim-secondary-body-frame.is-vetting {
    background: transparent;
    border: 0;
    box-shadow: none;
    padding: 8px 0 0;
  }

  .claim-secondary-body-frame.is-vetting .claim-vetting-surface {
    background: transparent;
    border: 0;
    box-shadow: none;
    padding: 0;
  }
}

@media only screen and (min-width: 701px) and (max-width: 992px) {
  .claim-view-shell {
    width: 92%;
  }

  .claim-dossier-card,
  .claim-status-panel,
  .claim-mode-strip,
  .claim-related-strip {
    padding: 22px;
  }

  .claim-mode-actions,
  .claim-related-strip {
    flex-wrap: nowrap;
    max-width: 100%;
    overflow-x: auto;
    padding-bottom: 4px;
  }

  .claim-mode-option {
    flex: 0 0 auto;
  }

  .claim-secondary-body-frame {
    padding: 18px;
  }

  .claim-secondary-body-frame.is-vetting {
    padding: 8px 0 0;
  }
}

@media only screen and (max-width: 700px) {
  .claim-view-container {
    width: calc(100% - 16px);
  }

  .claim-super-card {
    align-items: flex-start;
  }

  .claim-dossier-card,
  .claim-status-panel,
  .claim-related-strip {
    padding: 22px;
  }

  .claim-dossier-title h5 {
    font-size: 1.45rem;
  }

  .claim-dossier-title p {
    font-size: 0.94rem;
  }

  .claim-dossier-context {
    gap: 6px;
  }

  .claim-dossier-metrics,
  .status-grid {
    grid-template-columns: 1fr;
  }

  .metric-cell,
  .metric-cell:nth-child(3),
  .metric-cell:nth-child(4),
  .status-grid div {
    border-left: 0;
    border-top: 1px solid #e1e8eb;
  }

  .metric-cell:first-child,
  .status-grid div:first-child {
    border-top: 0;
  }

  .metric-action-cell {
    align-items: flex-start;
  }

  .download-action,
  .continue-vetting {
    justify-content: center;
    width: 100%;
  }

  .claim-mode-actions,
  .claim-vetting-tertiary {
    display: flex;
    flex-wrap: nowrap;
    max-width: 100%;
    overflow-x: auto;
    padding-bottom: 2px;
    scrollbar-width: none;
  }

  .claim-mode-actions::-webkit-scrollbar,
  .claim-vetting-tertiary::-webkit-scrollbar {
    display: none;
  }

  .claim-mode-option,
  .claim-vetting-tertiary-link {
    flex: 0 0 auto;
  }

  .claim-secondary-body-frame {
    padding: 12px;
  }

  .claim-secondary-body-frame.is-vetting {
    border-radius: 0;
    margin-top: 10px;
    padding: 6px 0 0;
  }

  .claim-secondary-body-frame.is-vetting .claim-vetting-tertiary {
    padding-left: 6px;
    padding-right: 6px;
  }

  .claim-secondary-body-frame.is-vetting .claim-vetting-tertiary-link {
    padding-left: 14px;
    padding-right: 14px;
  }

  .claim-secondary-body-frame :deep(.card-content),
  .claim-secondary-body-frame :deep(.card-panel) {
    padding: 16px;
  }

  .claim-secondary-body-frame :deep(.collection-item) {
    line-height: 1.45;
    padding: 12px 14px;
  }

  .continue-vetting {
    white-space: nowrap;
  }
}

@media (prefers-reduced-motion: reduce) {
  .claim-super-card,
  .claim-dossier-card,
  .claim-dossier-metrics,
  .claim-mode-strip,
  .claim-secondary-body-frame,
  .claim-vetting-surface,
  .encounter-review-row,
  .claim-total-strip,
  .claim-status-panel,
  .claim-review-workbench :deep(.vetting-soft-core),
  .claim-review-workbench :deep(.vetting-core-card),
  .claim-review-workbench :deep(.vetting-member-card),
  .claim-review-workbench :deep(.vetting-diagnosis-card),
  .claim-review-workbench :deep(.vetting-claims-card),
  .claim-review-workbench :deep(.vetting-edit-card) {
    animation: none;
  }
}
</style>
